// Control de caducidad de sesión (solo frontend).
//
// El backend firma el JWT con `expiresIn: '1d'` y responde 401 cuando el token
// ya no es válido. Aquí aprovechamos ese contrato para: (1) no enviar peticiones
// con un token ya caducado, (2) cerrar sesión en cuanto el backend devuelve 401
// y (3) programar el cierre automático en el momento exacto del `exp`, sin
// esperar a que el usuario haga clic en algo.

import { useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export const CLAVE_TOKEN = 'AuthToken';
const CLAVE_MOTIVO = 'SesionCaducada';
const RUTA_LOGIN = '/iniciar-sesion';
export const EVENTO_SESION_CADUCADA = 'sesion:caducada';

// Damos el token por caducado unos segundos antes de su `exp` real para no
// lanzar peticiones que el backend ya rechazaría por desfase de reloj.
const MARGEN_MS = 5000;
// Límite de setTimeout (int de 32 bits): por encima se dispara al instante.
const MAX_TIMEOUT_MS = 2147483647;

export const obtenerToken = () => localStorage.getItem(CLAVE_TOKEN);

// Devuelve el `exp` del token en milisegundos, o null si no se puede leer.
export const obtenerExpiracion = (token) => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// Token ausente o ilegible: caducado. Token sin `exp`: que decida el backend.
export const tokenCaducado = (token, margen = MARGEN_MS) => {
  if (!token) return true;

  let exp;
  try {
    exp = jwtDecode(token).exp;
  } catch {
    return true;
  }

  if (typeof exp !== 'number') return false;
  return Date.now() >= exp * 1000 - margen;
};

// Evita cierres/redirecciones en cascada cuando varias peticiones fallan a la vez.
let cerrandoSesion = false;

// `caducada`: true si el motivo es expiración del token (se muestra aviso en el
// login), false si simplemente ya no hay sesión (p. ej. logout en otra pestaña).
export const cerrarSesion = ({ caducada = true } = {}) => {
  if (cerrandoSesion) return;
  cerrandoSesion = true;

  localStorage.removeItem(CLAVE_TOKEN);
  if (caducada) {
    try {
      sessionStorage.setItem(CLAVE_MOTIVO, '1');
    } catch {
      // sessionStorage puede fallar en modo privado: el cierre sigue siendo válido.
    }
  } else {
    try {
      sessionStorage.removeItem(CLAVE_MOTIVO);
    } catch {
      // La navegación no depende de poder limpiar esta marca auxiliar.
    }
  }

  const rutaActual = window.location.pathname.replace(/\/$/, '');
  if (rutaActual === RUTA_LOGIN) {
    // Ya estamos en el login: no recargamos, solo avisamos a la pantalla.
    cerrandoSesion = false;
    if (caducada) window.dispatchEvent(new Event(EVENTO_SESION_CADUCADA));
    return;
  }

  // `replace` evita que el botón atrás devuelva a una pantalla privada.
  window.location.replace(RUTA_LOGIN);
};

// Lee y consume la marca de "sesión caducada". El resultado se cachea durante
// la carga de página para que varias llamadas (o el doble render de StrictMode)
// devuelvan siempre lo mismo.
let motivoConsumido = null;

export const consumirMotivoCaducidad = () => {
  if (motivoConsumido !== null) return motivoConsumido;

  try {
    motivoConsumido = sessionStorage.getItem(CLAVE_MOTIVO) !== null;
    if (motivoConsumido) sessionStorage.removeItem(CLAVE_MOTIVO);
  } catch {
    motivoConsumido = false;
  }
  return motivoConsumido;
};

const llevaToken = (config) =>
  Boolean(config?.headers?.Authorization || config?.headers?.authorization);

// Interceptores globales sobre la instancia por defecto de axios: todas las
// pantallas usan `import axios from 'axios'`, así que quedan cubiertas.
export const instalarInterceptoresSesion = () => {
  axios.interceptors.request.use((config) => {
    if (!llevaToken(config)) return config;

    const token = obtenerToken();
    if (tokenCaducado(token)) {
      cerrarSesion({ caducada: true });
      return Promise.reject(new axios.CanceledError('Sesión caducada'));
    }
    return config;
  });

  axios.interceptors.response.use(
    (respuesta) => respuesta,
    (error) => {
      // Solo reaccionamos al 401 de peticiones autenticadas: el login fallido
      // responde 404 y los endpoints públicos no envían Authorization.
      if (error?.response?.status === 401 && llevaToken(error.config)) {
        cerrarSesion({ caducada: true });
      }
      return Promise.reject(error);
    }
  );
};

// Vigila la sesión mientras el usuario está autenticado: temporizador hasta el
// `exp` + comprobación al recuperar el foco (los timers no corren fiables si el
// equipo se suspende) + sincronización entre pestañas.
export const useVigilanciaSesion = (activa) => {
  useEffect(() => {
    if (!activa) return undefined;

    let temporizador;

    const comprobar = () => {
      clearTimeout(temporizador);

      const token = obtenerToken();
      if (!token) {
        // La sesión se cerró en otra pestaña: salimos sin aviso de caducidad.
        cerrarSesion({ caducada: false });
        return;
      }
      if (tokenCaducado(token)) {
        cerrarSesion({ caducada: true });
        return;
      }

      const expiracion = obtenerExpiracion(token);
      if (expiracion === null) return;

      const espera = Math.max(0, expiracion - MARGEN_MS - Date.now());
      temporizador = setTimeout(comprobar, Math.min(espera + 100, MAX_TIMEOUT_MS));
    };

    const alRecuperarFoco = () => {
      if (document.visibilityState === 'visible') comprobar();
    };

    const alCambiarAlmacenamiento = (evento) => {
      if (evento.key === CLAVE_TOKEN || evento.key === null) comprobar();
    };

    comprobar();
    document.addEventListener('visibilitychange', alRecuperarFoco);
    window.addEventListener('focus', comprobar);
    window.addEventListener('storage', alCambiarAlmacenamiento);

    return () => {
      clearTimeout(temporizador);
      document.removeEventListener('visibilitychange', alRecuperarFoco);
      window.removeEventListener('focus', comprobar);
      window.removeEventListener('storage', alCambiarAlmacenamiento);
    };
  }, [activa]);
};
